/**
 * Stage Component - Main Game Logic (Dual-Mode Support)
 *
 * This is the core component that controls what each player sees during each round.
 * It implements the experimental design through conditional rendering based on
 * player markers, round names, and game mode.
 *
 * DUAL-MODE SUPPORT:
 * ------------------
 * Both modes use the same 9 rounds, but SINGLE mode auto-skips partner-dependent rounds.
 *
 * DYAD Mode (9 Rounds): Full 2-player experience with discussion
 *   - Round 0: Familiarity Survey
 *   - Round 1: Role Assignment (role + topic reveal)
 *   - Round 2: Pre-Learning Quiz (both answer the PRE half, closed-book baseline)
 *   - Round 3: Information Phase (Teacher learns with AI/PDF, Student views alt topic)
 *   - Round 4: Pre-Chat Preparation (Teacher: confidence survey, Student: rate alt topic)
 *   - Round 5: Student Examples (Student: pre-chat self-assessment, Teacher: waits)
 *   - Round 6: Communication Phase (chat between Teacher and Student)
 *   - Round 7: Post-Chat Reflection (both: self-assessment)
 *   - Round 8: Post-Learning Quiz (both answer the POST half + per-question battery)
 *
 * SINGLE Mode (9 Rounds, 2 auto-skipped): Solo learning without partner
 *   - Round 0: Familiarity Survey
 *   - Round 1: Role Assignment (auto-assigned as Learner)
 *   - Round 2: Pre-Learning Quiz (closed-book baseline)
 *   - Round 3: Information Phase (Learner uses AI/PDF)
 *   - Round 4: Pre-Chat Preparation (Learner: post-learning confidence survey)
 *   - Round 5: Student Examples → AUTO-SKIP (no partner)
 *   - Round 6: Communication Phase → AUTO-SKIP (no partner to chat with)
 *   - Round 7: Post-Chat Reflection (Learner: self-reflection on learning)
 *   - Round 8: Post-Learning Quiz
 *
 * Key Concepts:
 * ------------
 * - gameMode: "dyad" (2-player) or "single" (1-player)
 * - marker: In dyad mode, 0=Teacher, >0=Student. In single mode, always 0=Learner.
 * - secondary_marker: "ai", "ai-fixed", "pdf", "ai-pdf", or "web" determining information source
 * - Round dispatching uses round NAMES (not indices) for cross-mode compatibility
 * - AutoSubmitRound component used to skip rounds for SINGLE mode
 *
 * Debug Features:
 * --------------
 * - Console logging of marker, round, submission status
 * - Debug info displayed on waiting screen
 * - Safety checks for missing data
 */

import {
  usePlayer,
  usePlayers,
  useRound,
  useGame,
} from "@empirica/core/player/classic/react";
import { Loading } from "@empirica/core/player/react";
import React, { useEffect, useRef } from "react";
import { Task } from "./components/Task";
import { WebSearchTask } from "./components/WebSearchTask";
import { SocialExposure } from "./components/SocialExposure";
import { RoleInstructions } from "./components/RoleInstructions";
import { RoleAssignment } from "./components/RoleAssignment";
import { PostChatReflection } from "./components/PostChatReflection";
import { ExampleQuestions } from "./components/ExampleQuestions";
import { InstructionStepTwo } from "./intro-exit/InstructionStepTwo";
import { ComprehensionQuiz } from "./intro-exit/ComprehensionQuiz";
import { WaitingRoom } from "@/components/screens/WaitingRoom";
import { AlternativeTopicReview } from "./components/AlternativeTopicReview";
import { LearningPhase } from "./components/LearningPhase";
import { PreLearningQuiz } from "./components/PreLearningQuiz";
import { DevControls } from "./components/DevControls";
import { StayOnPageBanner } from "./components/StayOnPageBanner";

export function Stage() {
  const player = usePlayer();
  const players = usePlayers();
  const round = useRound();
  const game = useGame();

  const marker = player.get("marker");
  const secondaryMarker = player.get("secondary_marker");
  const currentRound = round.get("current");
  const roundName = round.get("name");
  const gameMode = game.get("gameMode") || "dyad";
  const isSingleMode = gameMode === "single";

  // Debug logging
  console.log("Stage render:", {
    playerId: player.id,
    marker,
    secondaryMarker,
    currentRound,
    roundName,
    gameMode,
    submitted: player.stage.get("submit"),
    playersCount: players.length
  });

  // Track phase start times (Information Phase, Communication Phase)
  // Using refs to track if we've already recorded the start time this render cycle
  // NOTE: hooks MUST stay above every conditional return — they previously sat
  // below the Loading/error returns, so when marker data arrived and the same
  // mounted component re-rendered down the full path, React threw "Rendered
  // more hooks than during the previous render" (white screen, data loss).
  const learningPhaseStartRecorded = useRef(false);
  const communicationPhaseStartRecorded = useRef(false);

  useEffect(() => {
    // Guard: marker may still be undefined while player data loads
    if (marker === undefined || marker === null) return;

    // Record start time for Information Phase - Teachers (marker=0) in DYAD, Learners in SINGLE
    // In SINGLE mode, marker is also 0, so this covers both cases
    if (roundName === "Information Phase" && marker === 0 && !learningPhaseStartRecorded.current) {
      const existingStartTime = player.get("learningPhaseStartTime");
      if (!existingStartTime) {
        const startTime = new Date().toISOString();
        player.set("learningPhaseStartTime", startTime);
        const roleLabel = isSingleMode ? "Learner" : "Teacher";
        console.log(`[Learning Phase] ${roleLabel} started at ${startTime} for player ${player.id}`);
      }
      learningPhaseStartRecorded.current = true;
    }

    // Record start time for Communication Phase (DYAD only) - only once per phase
    if (roundName === "Communication Phase" && !communicationPhaseStartRecorded.current) {
      const existingStartTime = player.get("communicationPhaseStartTime");
      if (!existingStartTime) {
        const startTime = new Date().toISOString();
        player.set("communicationPhaseStartTime", startTime);
        console.log(`[Communication Phase] Started at ${startTime} for player ${player.id}`);
      }
      communicationPhaseStartRecorded.current = true;
    }

    // Reset the refs when leaving the respective rounds
    if (roundName !== "Information Phase") {
      learningPhaseStartRecorded.current = false;
    }
    if (roundName !== "Communication Phase") {
      communicationPhaseStartRecorded.current = false;
    }
  }, [roundName, player, marker, isSingleMode]);

  // Safety check
  if (!round || !player.stage) {
    console.error("Missing round or stage data!");
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8 bg-red-100 rounded-lg shadow-lg">
          <h2 className="text-2xl font-semibold text-red-700 mb-2">
            Error: Game data not loaded
          </h2>
          <p className="text-red-600">Please refresh the page or contact support.</p>
        </div>
      </div>
    );
  }

  // Wait for marker AND secondaryMarker to be assigned before rendering round content
  // This prevents race conditions where round logic executes before player data is ready
  // Both values are set in callbacks.js onGameStart - they should be available together
  if (marker === undefined || marker === null || secondaryMarker === undefined || secondaryMarker === null) {
    console.log("Waiting for player data assignment...", { marker, secondaryMarker });
    return <Loading />;
  }

  if (player.stage.get("submit")) {
    console.log("Player submitted, waiting for others. Players:", players.length, "Stage remaining:", player.stage.get("remaining"));

    // Single mode: Show brief loading, stage will auto-advance
    if (isSingleMode) {
      return <Loading />;
    }

    // Dyad mode with only 1 player (shouldn't happen in production, but handle gracefully)
    if (players.length === 1 && !isSingleMode) {
      return <Loading />;
    }

    // Count how many players have submitted
    const submittedCount = players.filter(p => p.stage?.get("submit")).length;

    return (
      <>
        <StayOnPageBanner />
        <div className="h-full w-full flex flex-col">
          <div className="flex-1 min-h-0 w-full overflow-hidden">
            <WaitingRoom participantsReady={submittedCount} totalNeeded={players.length} />
          </div>
        </div>
      </>
    );
  }

  // Wrapper to include DevControls and StayOnPageBanner on every stage
  // StayOnPageBanner is fixed position, so we add pb-8 to prevent content overlap
  const withDevControls = (content) => (
    <>
      <StayOnPageBanner />
      <div className="h-full w-full flex flex-col">
        <div className="flex-1 min-h-0 w-full overflow-hidden">
          {content}
        </div>
      </div>
      <DevControls />
    </>
  );

  // ===========================================
  // ROUND DISPATCHING (by round name for dual-mode compatibility)
  // ===========================================

  // Familiarity Survey (all players, both modes)
  if (roundName === "Familiarity Survey") {
    return withDevControls(
      <div className="h-full w-full">
        <InstructionStepTwo
          next={() => {
            player.stage.set("submit", true);
          }}
        />
      </div>
    );
  }

  // Role Assignment
  // - DYAD: Both Teacher and Student see role only
  // - SINGLE: Learner sees role only
  if (roundName === "Role Assignment") {
    return withDevControls(
      <div className="h-full w-full">
        <RoleAssignment
          showExamples={false}
          next={() => {
            player.stage.set("submit", true);
          }}
        />
      </div>
    );
  }

  // Pre-Learning Quiz (all players, both modes) — closed-book baseline.
  // Both answer the PRE half before any learning; synchronized barrier.
  if (roundName === "Pre-Learning Quiz") {
    return withDevControls(
      <PreLearningQuiz
        questionIds={game.get("quizSplit")?.preIds || []}
        onComplete={() => player.stage.set("submit", true)}
      />
    );
  }

  // Ready button component for phases that allow early completion
  // Records timing data for learning phase (Information Phase) before submitting
  const ReadyButton = () => {
    const handleReadyClick = () => {
      // Record learning phase timing for Information Phase - Teachers (DYAD) or Learners (SINGLE)
      if (roundName === "Information Phase" && marker === 0) {
        const endTime = new Date().toISOString();
        player.set("learningPhaseEndTime", endTime);

        // Calculate duration in seconds
        const startTime = player.get("learningPhaseStartTime");
        if (startTime) {
          const durationMs = new Date(endTime) - new Date(startTime);
          const durationSeconds = Math.round(durationMs / 1000);
          player.set("learningPhaseDuration", durationSeconds);
          const roleLabel = isSingleMode ? "Learner" : "Teacher";
          console.log(`[Learning Phase] ${roleLabel} ended at ${endTime}, duration: ${durationSeconds}s for player ${player.id}`);
        }
      }

      // Record communication phase timing (DYAD only)
      if (roundName === "Communication Phase") {
        const endTime = new Date().toISOString();
        player.set("communicationPhaseEndTime", endTime);

        const startTime = player.get("communicationPhaseStartTime");
        if (startTime) {
          const durationMs = new Date(endTime) - new Date(startTime);
          const durationSeconds = Math.round(durationMs / 1000);
          player.set("communicationPhaseDuration", durationSeconds);
          console.log(`[Communication Phase] Ended at ${endTime}, duration: ${durationSeconds}s for player ${player.id}`);
        }
      }

      player.stage.set("submit", true);
    };

    return (
      <div className="flex-shrink-0 p-2 bg-gray-100 border-t border-gray-200">
        <button
          onClick={handleReadyClick}
          className="w-full py-2 px-3 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium rounded border border-gray-300 transition-colors flex items-center justify-center gap-2"
        >
          <span>I'm ready, continue</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    );
  };

  // Information Phase - Different conditions based on marker and secondary_marker
  // 10 minutes with option to proceed early via "I'm ready, continue" button
  // Two-column layout: Learning outcomes on left, material on right
  // Works for both DYAD (Teacher/Student) and SINGLE (Learner) modes
  if (roundName === "Information Phase") {
    // SINGLE mode OR Teacher (marker=0): Uses assigned learning condition
    // In single mode, marker is always 0 (Learner)
    if (isSingleMode || marker === 0) {
      // AI or AI-Fixed: AI Chat
      if (secondaryMarker === "ai" || secondaryMarker === "ai-fixed") {
        return withDevControls(
          <div className="h-full flex flex-col">
            <div className="flex-1 min-h-0">
              <LearningPhase>
                <div className="h-full">
                  <SocialExposure />
                </div>
              </LearningPhase>
            </div>
            <ReadyButton />
          </div>
        );
      }

      // PDF or AI-PDF: PDF viewer
      if (secondaryMarker === "pdf" || secondaryMarker === "ai-pdf") {
        return withDevControls(
          <div className="h-full flex flex-col">
            <div className="flex-1 min-h-0">
              <LearningPhase>
                <Task />
              </LearningPhase>
            </div>
            <ReadyButton />
          </div>
        );
      }

      // Web Search: Web Search interface
      if (secondaryMarker === "web") {
        return withDevControls(
          <div className="h-full flex flex-col">
            <div className="flex-1 min-h-0">
              <LearningPhase>
                <WebSearchTask />
              </LearningPhase>
            </div>
            <ReadyButton />
          </div>
        );
      }
    }

    // DYAD mode only: Student (marker > 0) views alternative topic PDF
    if (!isSingleMode && marker > 0) {
      return withDevControls(
        <div className="h-full flex flex-col">
          <div className="flex-1 min-h-0">
            <LearningPhase>
              <AlternativeTopicReview mode="view" />
            </LearningPhase>
          </div>
          <ReadyButton />
        </div>
      );
    }
  }

  // Pre-Chat Preparation (DYAD mode only)
  // - Teacher: Pre-chat self-assessment (via RoleInstructions)
  // - Student: Rate the alternative topic PDF + see role instructions
  if (roundName === "Pre-Chat Preparation") {
    // Student (marker > 0): Rate alt topic AND see role instructions (combined view)
    if (marker > 0) {
      return withDevControls(
        <AlternativeTopicReview
          mode="rate"
          showInstructions={true}
          next={() => {
            player.stage.set("submit", true);
          }}
        />
      );
    }

    // Teacher (marker = 0): Pre-chat self-assessment (handled by RoleInstructions)
    return withDevControls(
      <div className="h-full w-full">
        <RoleInstructions
          next={() => {
            player.stage.set("submit", true);
          }}
        />
      </div>
    );
  }

  // Student Examples (DYAD mode only)
  // Student sees example questions, Teacher confirms ready to teach
  // SINGLE mode: Auto-skip (no partner interaction needed)
  if (roundName === "Student Examples") {
    if (isSingleMode) {
      return <AutoSubmitRound player={player} message="Continuing..." withDevControls={withDevControls} />;
    }
    if (marker > 0) {
      // Student sees example questions
      return withDevControls(
        <div className="h-full w-full">
          <ExampleQuestions
            next={() => {
              player.stage.set("submit", true);
            }}
          />
        </div>
      );
    }
    // Teacher sees "Ready to Teach" confirmation
    return withDevControls(
      <div className="h-full w-full">
        <RoleInstructions
          next={() => {
            player.stage.set("submit", true);
          }}
        />
      </div>
    );
  }

  // Communication Phase (DYAD mode only)
  // Teacher explains to Student via chat
  // SINGLE mode: Auto-skip (no partner to chat with)
  if (roundName === "Communication Phase") {
    if (isSingleMode) {
      return <AutoSubmitRound player={player} message="Continuing to reflection..." withDevControls={withDevControls} />;
    }
    return withDevControls(
      <div className="h-full w-full flex flex-col">
        <div className="flex-1 min-h-0">
          <SocialExposure />
        </div>
        <ReadyButton />
      </div>
    );
  }

  // Post-Chat Reflection
  // DYAD: Self-assessment after discussion
  // SINGLE: Self-reflection on learning (handled by PostChatReflection with isLearner check)
  if (roundName === "Post-Chat Reflection") {
    return withDevControls(
      <div className="h-full w-full">
        <PostChatReflection
          next={() => {
            player.stage.set("submit", true);
          }}
        />
      </div>
    );
  }

  // Post-Learning Quiz (all players, both modes) — the POST half + full battery
  if (roundName === "Post-Learning Quiz") {
    return withDevControls(
      <div className="h-full w-full">
        <ComprehensionQuiz
          next={() => {
            player.stage.set("submit", true);
          }}
        />
      </div>
    );
  }

  // Fallback (shouldn't reach here)
  return withDevControls(
    <div className="flex items-center justify-center h-full">
      <div className="text-center p-8 bg-gray-100 rounded-lg shadow-lg">
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">
          Loading...
        </h2>
      </div>
    </div>
  );
}

/**
 * AutoSubmitRound Component
 * Used to skip rounds that are no longer needed (e.g., Self-Reflection for singles)
 */
function AutoSubmitRound({ player, message, withDevControls }) {
  useEffect(() => {
    if (!player.stage.get("submit")) {
      player.stage.set("submit", true);
    }
  }, [player]);

  return withDevControls(
    <div className="h-full w-full flex items-center justify-center">
      <p className="text-gray-500">{message}</p>
    </div>
  );
}
